/**
 * SemaphoreWithPriority is a utility class designed to manage and prioritize
 * asynchronous function calls. It maintains three separate queues for high,
 * normal, and low priority function calls and ensures that they are executed
 * based on their priority and the availability of resources.
 *
 * The class also provides a caching mechanism to prevent duplicate function
 * calls with the same arguments. If a function call with the same arguments
 * is already in the queue, the new call will wait for the result of the existing
 * call and then resolve or reject accordingly.
 *
 * Features:
 * - Limit the number of concurrent function calls.
 * - Prioritize function calls based on three levels: high, normal, and low.
 * - Cache pending requests to avoid duplicate processing.
 * - Optional delay between function calls.
 *
 * Usage:
 * const semaphore = new SemaphoreWithPriority(2); // max 2 concurrent requests
 * semaphore.callFunction(someAsyncFunction, arg1, arg2);
 * semaphore.callFunctionHighPriority(anotherAsyncFunction, arg1);
 * semaphore.callFunctionLowPriority(yetAnotherAsyncFunction, arg1, arg2, arg3);
 */
const SEM_DEBUG = true;
const semLog = (msg: string, data?: unknown) => SEM_DEBUG && console.log(`[Semaphore] ${msg}`, data !== undefined && data !== null && typeof data === 'object' ? JSON.stringify(data) : (data ?? ''));

export class Semaphore {
  currentRequestsHigh: any[] = [];
  currentRequestsNormal: any[] = [];
  currentRequestsLow: any[] = [];
  runningRequests: number = 0;
  maxConcurrentRequests: number;
  delay: number;
  pendingRequestsCache: { [key: string]: any[] } = {};

  constructor(maxConcurrentRequests: number = 1, delay: number = 0) {
    this.maxConcurrentRequests = maxConcurrentRequests;
    this.delay = delay;
  }

  reset() {
    // Reject all pending waiters so their promises don't hang forever.
    for (const key of Object.keys(this.pendingRequestsCache)) {
      this.pendingRequestsCache[key]?.forEach(({ reject }) =>
        reject(new Error('Semaphore reset')),
      );
    }
    this.currentRequestsHigh.length = 0;
    this.currentRequestsNormal.length = 0;
    this.currentRequestsLow.length = 0;
    this.runningRequests = 0;
    this.pendingRequestsCache = {};
  }

  wrap<T extends Array<any>, U>(fnToCall: (...args: T) => Promise<U>) {
    return (...args: T): Promise<U> => this.callFunction(fnToCall, ...args);
  }

  private enqueueRequest<T extends Array<any>, U>(
    priorityQueue: any[],
    fnToCall: (...args: T) => Promise<U>,
    ...args: T
  ): Promise<U> {
    return new Promise((resolve, reject) => {
      const key = `${fnToCall.name}-${JSON.stringify(args)}-${JSON.stringify((fnToCall as any).id || '')}`;
      if (this.pendingRequestsCache[key]) {
        semLog(`cache HIT — coalescing request (${this.pendingRequestsCache[key].length + 1} waiters)`, { fn: fnToCall.name });
        this.pendingRequestsCache[key].push({ resolve, reject });
        return;
      }
      this.pendingRequestsCache[key] = [{ resolve, reject }];

      priorityQueue.push({
        fnToCall,
        args,
        key,
        createdAt: (fnToCall as any).createdAt || new Date()
      });
      this.tryNext();
    });
  }

  callFunction<T extends Array<any>, U>(
    fnToCall: (...args: T) => Promise<U>,
    ...args: T
  ): Promise<U> {
    return this.enqueueRequest(this.currentRequestsNormal, fnToCall, ...args);
  }

  callFunctionHighPriority<T extends Array<any>, U>(
    fnToCall: (...args: T) => Promise<U>,
    ...args: T
  ): Promise<U> {
    return this.enqueueRequest(this.currentRequestsHigh, fnToCall, ...args);
  }

  callFunctionLowPriority<T extends Array<any>, U>(
    fnToCall: (...args: T) => Promise<U>,
    ...args: T
  ): Promise<U> {
    return this.enqueueRequest(this.currentRequestsLow, fnToCall, ...args);
  }

  tryNext() {
    [this.currentRequestsHigh, this.currentRequestsNormal, this.currentRequestsLow].forEach(queue => {
      for (let i = 0; i < queue.length; i++) {
        const { key } = queue[i];
        if (!this.pendingRequestsCache[key]) {
          queue.splice(i, 1);
          i--;
        }
      }
    });

    if (
      !this.currentRequestsHigh.length &&
      !this.currentRequestsNormal.length &&
      !this.currentRequestsLow.length
    ) {
      return;
    } else if (this.runningRequests < this.maxConcurrentRequests) {
      // Intentional LIFO: newest requests first (b - a) to prioritize latest user actions
      const sortByCreatedAt = (a: any, b: any) => b.createdAt - a.createdAt;
      this.currentRequestsHigh.sort(sortByCreatedAt);
      this.currentRequestsNormal.sort(sortByCreatedAt);
      this.currentRequestsLow.sort(sortByCreatedAt);

      const pickedFrom = this.currentRequestsHigh.length ? 'HIGH' : this.currentRequestsNormal.length ? 'NORMAL' : 'LOW';
      let { fnToCall, args, key } =
        this.currentRequestsHigh.shift() ||
        this.currentRequestsNormal.shift() ||
        this.currentRequestsLow.shift();
      this.runningRequests++;
      semLog(`pick ${pickedFrom} — running=${this.runningRequests}/${this.maxConcurrentRequests}, queued=[H:${this.currentRequestsHigh.length} N:${this.currentRequestsNormal.length} L:${this.currentRequestsLow.length}]`);

      let req = fnToCall(...args);

      req
        .then((res: any) => {
          this.pendingRequestsCache[key]?.forEach(({ resolve }) => resolve(res));
          delete this.pendingRequestsCache[key];
        })
        .catch((err: any) => {
          this.pendingRequestsCache[key]?.forEach(({ reject }) => reject(err));
          delete this.pendingRequestsCache[key];
        })
        .finally(() => {
          setTimeout(() => {
            this.runningRequests--;
            this.tryNext();
          }, this.delay);
        });
    }
  }
}
